import React, { Fragment, ReactNode } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { isCashApp } from 'features/payment/utils';
import { camelCase, capitalize, isNil } from 'lodash-es';

import { ILegacyCartEntry, IServerOrder } from '@rbi-ctg/menu';
import { DiscountTypes } from 'enums/menu';
import { LoyaltyPromotionType, ServiceMode } from 'generated/graphql-gateway';
import { OfferFeedbackAdditionalInfo } from 'pages/cart/cart-item/hooks/use-feedback-additional-info';
import { StyledLink } from 'pages/cart/cart-item/styled';
import { formatDate, formatTime, t, tRich } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { CartEntryType } from 'utils/cart/types';

import {
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
} from '../types';

import { makeIncentiveOutOfDayPartMessage } from './incentives-availability';

interface IEvaluationResultMessageParams {
  evaluationResult: IIncentiveEvaluationResult;
  incentiveType: LoyaltyPromotionType;
  cartEntries: ILegacyCartEntry[];
  experimentalMessages?: { [key: string]: string };
  forceOnlyStringMessage?: boolean;
  additionalInfo?: OfferFeedbackAdditionalInfo;
}
export const getEvaluationResultMessage = ({
  evaluationResult,
  incentiveType: type,
  cartEntries,
  experimentalMessages,
  forceOnlyStringMessage = false,
  additionalInfo,
}: IEvaluationResultMessageParams): string | ReactNode => {
  const { code, currentValue, targetValue } = evaluationResult;
  const incentiveType = type.toLowerCase();
  let message: string | ReactNode = t('incentiveFeedbackDefault', { incentiveType });

  switch (code) {
    case IncentiveEvaluationErrorCodes.BELOW_MINIMUM_SPEND: {
      if (isNil(currentValue) || isNil(targetValue)) {
        break;
      }

      const translationKeyId = experimentalMessages?.[code] ?? 'incentiveFeedbackSubtotal';
      message = t(translationKeyId as TranslationKey, {
        incentiveType,
        currentValue: `${currentValue / 100}`,
        targetValue: `${targetValue / 100}`,
        calculatedValue: `${(targetValue - currentValue) / 100}`,
      });

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_DAY_OF_THE_WEEK: {
      if (!targetValue?.length) {
        break;
      }

      if (forceOnlyStringMessage) {
        const days = targetValue.map((day: string) => t(day as TranslationKey)).join(', ');
        message = t('incentiveFeedbackInvalidDay', {
          incentiveType,
          targetValue: days,
        });
      } else {
        message = tRich('incentiveFeedbackInvalidDay', {
          incentiveType,
          targetValue:
            targetValue.length > 1 ? (
              <Box>
                {targetValue.map((day: string) => (
                  <Box key={day}>{`${t(day as TranslationKey)}`}</Box>
                ))}
              </Box>
            ) : (
              t(targetValue[0] as TranslationKey)
            ),
        });
      }
      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_TIME_SPAN: {
      const { endTime, startTime } = targetValue;
      if (!startTime || !endTime) {
        break;
      }

      message = t('incentiveFeedbackInvalidTime', {
        incentiveType,
        startTime: formatTime(timeToDate(startTime)),
        endTime: formatTime(timeToDate(endTime)),
      });
      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_END_DATE: {
      const { endDate } = evaluationResult.targetValue;
      if (!endDate) {
        break;
      }

      message = t('incentiveFeedbackExpired', {
        incentiveType,
        endDate: formatDate(endDate, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      });

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE: {
      const serviceModes = targetValue;
      if (!serviceModes?.length) {
        break;
      }

      const messageKey = serviceModes.some((sm: ServiceMode) => sm === ServiceMode.DELIVERY)
        ? ServiceMode.DELIVERY
        : ServiceMode.TAKEOUT;

      message = t('incentiveFeedbackServiceMode', {
        incentiveType,
        targetValue: t(serviceModeLocalizedKeysMap[messageKey] as TranslationKey),
      });

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD: {
      const paymentMethod = targetValue;
      if (!paymentMethod) {
        break;
      }

      const isCashAppPaymentMethod = isCashApp(paymentMethod);

      const paymentMethodKey = isCashAppPaymentMethod
        ? 'cashAppPay'
        : (camelCase(paymentMethod) as TranslationKey);
      const formattedMessage = t(paymentMethodKey);

      const translationKeyId = experimentalMessages?.[code] ?? 'incentiveFeedbackPaymentMethod';
      message = t(translationKeyId as TranslationKey, {
        incentiveType,
        targetValue: isCashAppPaymentMethod ? formattedMessage : capitalize(formattedMessage),
      });

      break;
    }

    case IncentiveEvaluationErrorCodes.EXCEEDS_MAX_REDEMPTION: {
      message = t('incentiveFeedbackMaxRedemptions', { incentiveType });
      break;
    }

    case IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS_EXCLUSION: {
      const exclusionsIds: string[] = evaluationResult.targetValue.map(
        (incentive: { id: string }) => incentive.id
      );

      const exclusionsNames = cartEntries
        .filter(entry => exclusionsIds.includes(entry._id))
        .map(entry => entry.name);

      if (forceOnlyStringMessage) {
        const fixedMessage = t('incentiveFeedbackCartRequirementExclusion');
        message = `${fixedMessage} ${exclusionsNames.join(', ')}`;
      } else {
        message = (
          <>
            {t('incentiveFeedbackCartRequirementExclusion').trim()}{' '}
            <Text fontWeight="bold">{exclusionsNames.join(', ')}</Text>
          </>
        );
      }
      break;
    }

    case IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS: {
      const feedbackAdditionalInfo =
        additionalInfo?.[IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS];

      if (!feedbackAdditionalInfo) {
        const translationKeyId = experimentalMessages?.[code] ?? 'incentiveFeedbackCartRequirement';
        message = t(translationKeyId as TranslationKey, { incentiveType });
      } else {
        message = (
          <>
            {t('add')}{' '}
            {feedbackAdditionalInfo.map((rule, ruleIndex) => (
              <Fragment key={ruleIndex}>
                {!!ruleIndex && <> {t('and')} </>}
                {rule.map(({ id, name, quantity, onPress }, innerIndex) => {
                  return (
                    <Fragment key={id}>
                      {!!innerIndex && <> {t('or')} </>}
                      <StyledLink onPress={onPress}>
                        {quantity} {name}
                      </StyledLink>
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}{' '}
            {t('unlockCartReq')}
          </>
        );
      }

      break;
    }

    case IncentiveEvaluationErrorCodes.INVALID_STORE_ID: {
      message = t('incentiveFeedbackInvalidStore', { incentiveType });
      break;
    }

    case IncentiveEvaluationErrorCodes.OUT_OF_DAY_PART: {
      const { incentive, dayParts } = targetValue;
      message = makeIncentiveOutOfDayPartMessage({ incentive, dayParts });
      break;
    }

    case IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE: {
      message = t('incentiveFeedbackNotAvailableInStore');
      break;
    }

    case IncentiveEvaluationErrorCodes.OFFER_NOT_AVAILABLE: {
      message = t('incentiveFeedbackOfferNotAvailable');
      break;
    }

    case IncentiveEvaluationErrorCodes.REWARD_NOT_AVAILABLE: {
      message = t('incentiveFeedbackNotAvailableInStore');
      break;
    }

    case IncentiveEvaluationErrorCodes.WITHIN_COOL_DOWN_PERIOD: {
      const calculatedValue = differenceInMinutes(new Date(targetValue), new Date(currentValue));

      if (!isNaN(calculatedValue)) {
        message = t('incentiveFeedbackWithinCooldown', { calculatedValue, incentiveType });
      }
      break;
    }

    // All errors will be transformed into messages here
    default: {
      return message;
    }
  }

  return message;
};

export const createFeedbackMaps = (
  incentives: readonly { id: string; errors?: Readonly<IncentiveEvaluationMap[string]> }[]
): {
  incentiveFeedbackMap: IncentiveEvaluationMap;
  incentivesMapById: { [key in string]: string };
} => {
  const { incentiveFeedbackMap, incentivesMapById } = incentives.reduce(
    (acc, incentive) => {
      const incentiveId = incentive.id;

      acc.incentivesMapById[incentiveId] = incentiveId;
      if (incentive?.errors?.length) {
        acc.incentiveFeedbackMap[incentiveId] = incentive.errors;
      }

      return acc;
    },
    { incentiveFeedbackMap: {}, incentivesMapById: {} }
  );

  return { incentiveFeedbackMap, incentivesMapById };
};

const timeToDate = (time: string): Date => {
  const today = new Date();

  const [hr, min, sec] = time.split(':');
  today.setHours(+hr);
  today.setMinutes(+min);
  today.setSeconds(+sec);

  return today;
};

const serviceModeLocalizedKeysMap = {
  [ServiceMode.DELIVERY]: 'delivery',
  [ServiceMode.DRIVE_THRU]: 'driveThru',
  [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: 'driveThru',
  [ServiceMode.EAT_IN]: 'eatIn',
  [ServiceMode.TAKEOUT]: 'pickUp',
};

export const getServerOrderSubtotalBeforeOfferDiscounts = (serverOrder: IServerOrder | null) => {
  if (!serverOrder?.cart) {
    return 0;
  }
  const hasOfferDiscountApplied = serverOrder.cart?.cartEntries?.some(
    entry => entry.type === CartEntryType.offerDiscount
  );
  const orderDiscounts = serverOrder.cart?.discounts ?? [];
  const orderSubtotalCents = serverOrder.cart?.subTotalCents ?? 0;
  const offersDiscountsCents = orderDiscounts.reduce(
    (acc, discount) =>
      discount.name === DiscountTypes.COMBO_AND_OFFER_DISCOUNTS ? acc + discount.value : acc,
    0
  );

  return hasOfferDiscountApplied ? orderSubtotalCents + offersDiscountsCents : orderSubtotalCents;
};

/**
 * Push Offers with inline alert messages to the bottom
 */
export const pushUnavailableOffersToBottom = (a: Record<string, any>, b: Record<string, any>) => {
  if (!a?.inlineAlertMessage && b?.inlineAlertMessage) {
    return -1;
  } else if (a?.inlineAlertMessage && !b?.inlineAlertMessage) {
    return 1;
  }
  return 0;
};

export const getNextQuarterDate = (): Date => {
  // NOTE: custom override for 2024 to only allow for one redemption in Q3 and Q4 2024
  const currentDate = new Date().getUTCFullYear() === 2024 ? new Date('2024-11-01') : new Date();
  const currentMonth = currentDate.getMonth(); // getMonth returns month index starting from 0 (Jan)

  // Determine the start month of the next quarter
  const nextQuarterStartMonth = ((Math.floor(currentMonth / 3) + 1) % 4) * 3;

  // Handle year transition
  const nextQuarterYear =
    nextQuarterStartMonth === 0 ? currentDate.getFullYear() + 1 : currentDate.getFullYear();

  // Create a new date object for the first day of the next quarter
  const nextQuarterDate = new Date(nextQuarterYear, nextQuarterStartMonth, 1);

  return nextQuarterDate;
};

export const getTomorrowDate = (): Date => {
  const currentDate = new Date();
  const tomorrowDate = new Date(currentDate);
  tomorrowDate.setDate(currentDate.getDate() + 1);
  return tomorrowDate;
};
